'use client';
import React from 'react';

import Icon from '@/components/ui/AppIcon';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const TENANTS = [
  { name: 'Larsen & Toubro — Construction', plan: 'Professional', users: 12, sites: 4, status: 'active', mrr: '€ 650', joined: 'Jan 2026' },
  { name: 'Gulf Contracting Co.', plan: 'Operational', users: 8, sites: 2, status: 'active', mrr: '€ 350', joined: 'Mar 2026' },
  { name: 'Nordic Warehousing AS', plan: 'Starter', users: 3, sites: 1, status: 'trial', mrr: '€ 0', joined: 'Jul 2026' },
  { name: 'Iberia Logistics SL', plan: 'Operational', users: 7, sites: 3, status: 'active', mrr: '€ 350', joined: 'Apr 2026' },
  { name: 'Adriatic EPC d.o.o.', plan: 'Professional', users: 18, sites: 5, status: 'active', mrr: '€ 650', joined: 'Feb 2026' },
];

const MRR_DATA = [
  { month: 'Feb', mrr: 1300 },
  { month: 'Mar', mrr: 1650 },
  { month: 'Apr', mrr: 2300 },
  { month: 'May', mrr: 2300 },
  { month: 'Jun', mrr: 2650 },
  { month: 'Jul', mrr: 3000 },
];

const PLAN_COLOR: Record<string, string> = {
  Starter: 'bg-slate-100 text-slate-600',
  Operational: 'bg-blue-100 text-blue-700',
  Professional: 'bg-violet-100 text-violet-700',
  Enterprise: 'bg-amber-100 text-amber-700',
};

export default function SuperAdminDashboard() {
  return (
    <div className="space-y-5">
      {/* Role header */}
      <div className="flex items-center gap-3 px-5 py-4 bg-gradient-to-r from-rose-700 to-rose-900 rounded-2xl text-white">
        <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
          <Icon name="ServerStackIcon" size={22} />
        </div>
        <div>
          <p className="font-700 text-base">Platform Administration</p>
          <p className="text-[12px] text-white/65">Super Admin · theopsivo.com · Platform-wide access</p>
        </div>
        <div className="ml-auto flex gap-2">
          <button className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="PlusIcon" size={13} /> New Tenant
          </button>
          <button className="flex items-center gap-1.5 px-3 py-1.5 bg-accent text-accent-foreground hover:bg-amber-400 rounded-lg text-[12px] font-600 transition-colors">
            <Icon name="DocumentChartBarIcon" size={13} /> Platform Report
          </button>
        </div>
      </div>

      {/* Platform KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Active Tenants', value: '5', sub: '1 on trial', icon: 'BuildingOffice2Icon', color: 'bg-rose-50 border-rose-200', iconColor: 'bg-rose-100 text-rose-600' },
          { label: 'Monthly Recurring Revenue', value: '€ 3,000', sub: '+€350 vs Jun', icon: 'CurrencyEuroIcon', color: 'bg-teal/5 border-teal/20', iconColor: 'bg-teal/10 text-teal' },
          { label: 'Total Active Users', value: '48', sub: 'Across all tenants', icon: 'UsersIcon', color: 'bg-secondary/5 border-secondary/20', iconColor: 'bg-secondary/10 text-secondary' },
          { label: 'Platform Uptime', value: '99.9%', sub: 'Last 30 days', icon: 'ServerIcon', color: 'bg-emerald-50 border-emerald-200', iconColor: 'bg-emerald-100 text-emerald-600' },
        ].map((k) => (
          <div key={k.label} className={`rounded-xl border p-4 shadow-card ${k.color}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${k.iconColor}`}>
              <Icon name={k.icon as Parameters<typeof Icon>[0]['name']} size={18} />
            </div>
            <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground">{k.label}</p>
            <p className="text-2xl font-700 text-foreground tabular-nums mt-0.5">{k.value}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{k.sub}</p>
          </div>
        ))}
      </div>

      {/* MRR chart */}
      <div className="bg-card border border-border rounded-2xl shadow-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-600 text-foreground">MRR Growth</h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">Monthly recurring revenue (€)</p>
          </div>
          <span className="text-[11px] text-teal font-600 bg-teal/8 px-2.5 py-1 rounded-lg border border-teal/15">↑ 130% since Feb</span>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <BarChart data={MRR_DATA} barSize={32}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E4E9F2" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 10, fill: '#64748B' }} axisLine={false} tickLine={false} tickFormatter={(v) => `€${v}`} />
            <Tooltip formatter={(v: number) => [`€${v.toLocaleString()}`, 'MRR']} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #D1D9E6' }} />
            <Bar dataKey="mrr" fill="#BE123C" radius={[5, 5, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Tenant list */}
      <div className="bg-card border border-border rounded-2xl shadow-card">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Icon name="BuildingOffice2Icon" size={15} className="text-rose-600" />
            <h3 className="text-sm font-600 text-foreground">All Tenants</h3>
            <span className="text-[10px] bg-rose-50 text-rose-600 border border-rose-200 px-2 py-0.5 rounded-full font-600">5 tenants</span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {['Tenant', 'Plan', 'Users', 'Sites', 'MRR', 'Joined', 'Status', ''].map((h) => (
                  <th key={h} className="text-left text-[10px] font-600 uppercase tracking-wider text-muted-foreground px-5 py-2.5">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {TENANTS.map((t) => (
                <tr key={t.name} className="hover:bg-muted/20 transition-colors">
                  <td className="px-5 py-3">
                    <p className="text-[12px] font-600 text-foreground">{t.name}</p>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-600 ${PLAN_COLOR[t.plan]}`}>{t.plan}</span>
                  </td>
                  <td className="px-5 py-3 text-[12px] text-foreground tabular-nums">{t.users}</td>
                  <td className="px-5 py-3 text-[12px] text-foreground tabular-nums">{t.sites}</td>
                  <td className="px-5 py-3 text-[12px] font-600 text-foreground tabular-nums">{t.mrr}</td>
                  <td className="px-5 py-3 text-[11px] text-muted-foreground">{t.joined}</td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-600 ${t.status === 'active' ? 'bg-teal/10 text-teal' : 'bg-amber-100 text-amber-700'}`}>{t.status}</span>
                  </td>
                  <td className="px-5 py-3">
                    <button className="text-[11px] text-secondary hover:underline font-500">Manage</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
